import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, Music4, Mail, CalendarDays } from "lucide-react";
import { IMAGES, PROMO, CONTACTS, waLink } from "@/data/site";

const scrollTo = (id) =>
  document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Lezione di pianoforte"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/90 to-ink-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-ink-900/60" />
        <div className="grain-overlay" />
      </div>

      <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/40 bg-gold-500/10 px-4 py-1.5 mb-7">
            <Music4 size={15} className="text-gold-500" />
            <span className="text-xs uppercase tracking-[0.2em] text-gold-400">
              Iscrizioni aperte · A.S. {PROMO.schoolYear}
            </span>
          </div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-white">
            Da qui si arriva
            <br />
            <span className="italic text-gold-500">sul palcoscenico.</span>
          </h1>

          <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-gray-300">
            La scuola di musica e spettacolo tra Ferrara e Rovigo con
            certificazioni internazionali{" "}
            <span className="text-white font-medium">Trinity College London</span>.
            Iscrizioni aperte per il nuovo anno: contattaci per tutte le informazioni.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-preiscrizione-btn"
              onClick={() => scrollTo("#contatti")}
              className="group inline-flex items-center justify-between gap-3 rounded-2xl bg-gold-500 text-ink-900 px-7 py-4 text-left hover:bg-gold-400 hover:-translate-y-1 transition-transform duration-200 gold-glow"
            >
              <span>
                <span className="block font-semibold">Iscriviti ora</span>
                <span className="block text-xs font-medium text-ink-900/70">
                  ed inizia il tuo corso dal 3 settembre
                </span>
              </span>
              <ArrowRight size={18} className="shrink-0 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <a
              data-testid="hero-whatsapp-btn"
              href={waLink("Ciao, vorrei informazioni sui corsi e le iscrizioni 2026/2027 di Trinity Musical School")}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center justify-start gap-3 rounded-2xl border border-white/20 text-white px-7 py-4 text-left hover:border-gold-500 hover:text-gold-500 transition-colors duration-200"
            >
              <MessageCircle size={18} className="shrink-0" />
              <span>
                <span className="block font-medium">Scrivici su WhatsApp</span>
                <span className="block text-xs text-gray-400 group-hover:text-gold-500/80 transition-colors duration-200">
                  per informazioni e per prenotare una lezione prova gratuita
                </span>
              </span>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-400">
            <span>★★★★★ Centro esami Trinity n° {CONTACTS.centre}</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <span>200+ allievi · 3 sedi · dal 2019</span>
          </div>
        </motion.div>

        {/* Right info card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div
            data-testid="promo-card"
            className="relative rounded-3xl border border-gold-500/30 bg-ink-800/70 backdrop-blur-xl p-8 sm:p-10 gold-glow"
          >
            <div className="absolute -top-3 left-8 rounded-full bg-crimson px-4 py-1 text-xs uppercase tracking-widest text-white">
              Iscrizioni aperte
            </div>
            <p className="text-sm uppercase tracking-[0.2em] text-gray-400">
              Anno scolastico
            </p>
            <div className="mt-2 font-serif text-6xl sm:text-7xl leading-none text-gold-500">
              {PROMO.schoolYear}
            </div>

            <div className="mt-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-ink-900/60 px-5 py-4">
              <CalendarDays size={22} className="text-gold-500 shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">
                  Inizio lezioni
                </p>
                <p className="text-lg font-semibold text-white">{PROMO.startLabel}</p>
              </div>
            </div>

            <div className="my-7 h-px bg-white/10" />

            <p className="text-gray-300">
              Contattaci per informazioni sui corsi, le sedi e la prima lezione
              conoscitiva gratuita.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <a
                data-testid="promo-card-whatsapp"
                href={waLink("Ciao, vorrei informazioni sui corsi e le iscrizioni 2026/2027 di Trinity Musical School")}
                target="_blank"
                rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-semibold py-3.5 hover:-translate-y-0.5 transition-transform duration-200"
              >
                <MessageCircle size={18} /> Contattaci su WhatsApp
              </a>
              <button
                data-testid="promo-card-btn"
                onClick={() => scrollTo("#contatti")}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 text-ink-900 font-semibold py-3.5 hover:bg-gold-400 transition-colors duration-200"
              >
                <Mail size={18} /> Richiedi informazioni
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">
              Prima lezione conoscitiva gratuita
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
